"use client";

import { useDeferredValue, useMemo, useState } from "react";
import type { ActivityDefinition } from "@/lib/activities";
import { getActivitySessionRules } from "@/lib/activity-config";
import {
  type ActivityPickerFilter,
  type ActivityPickerItem,
  MAX_RECENT_SESSION_ACTIVITIES,
  QUICK_PICK_ACTIVITY_IDS,
  RECENT_SESSION_ACTIVITY_STORAGE_KEY,
} from "./activity-session-drawer.constants";
import {
  buildActivityPickerSubtitle,
  scoreActivitySearchMatch,
} from "./activity-session-drawer.utils";

const readRecentActivityIds = (): string[] => {
  try {
    const stored = window.localStorage.getItem(
      RECENT_SESSION_ACTIVITY_STORAGE_KEY,
    );
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((v): v is string => typeof v === "string")
      .slice(0, MAX_RECENT_SESSION_ACTIVITIES);
  } catch {
    return [];
  }
};

export function useActivityPicker(activities: ActivityDefinition[]) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<ActivityPickerFilter>("popular");
  const [recentActivityIds, setRecentActivityIds] = useState<string[]>(
    readRecentActivityIds,
  );
  const deferredQuery = useDeferredValue(query);
  const normalizedQuery = deferredQuery.trim().toLowerCase();

  const allItems = useMemo(() => {
    const recentSet = new Set(recentActivityIds);
    const quickPickSet = new Set<string>(QUICK_PICK_ACTIVITY_IDS);

    return [...activities]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((activity) => {
        const rules = getActivitySessionRules(activity);
        const category =
          rules?.archetype === "scored_match" ? "matches" : "sessions";
        return {
          activity,
          category,
          subtitle: buildActivityPickerSubtitle(activity),
          searchText: [
            activity.id,
            activity.name,
            activity.metric,
            rules?.archetype === "scored_match"
              ? "match result score competitive casual verified"
              : "session social group joinable no score",
            rules?.session.formats.join(" "),
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase(),
          isPopular: quickPickSet.has(activity.id),
          isRecent: recentSet.has(activity.id),
        } satisfies ActivityPickerItem;
      });
  }, [activities, recentActivityIds]);

  const itemMap = useMemo(
    () =>
      new Map<string, ActivityPickerItem>(
        allItems.map((item) => [item.activity.id, item]),
      ),
    [allItems],
  );

  const popularItems = useMemo(() => {
    const items: ActivityPickerItem[] = [];
    const seen = new Set<string>();

    const push = (item: ActivityPickerItem | undefined) => {
      if (!item || seen.has(item.activity.id)) return;
      seen.add(item.activity.id);
      items.push(item);
    };

    for (const id of QUICK_PICK_ACTIVITY_IDS) push(itemMap.get(id));
    for (const item of allItems) {
      // Three full rows of the three-column grid.
      if (items.length >= 9) break;
      push(item);
    }
    return items;
  }, [itemMap, allItems]);

  const recentItems = useMemo(
    () =>
      recentActivityIds
        .map((id) => itemMap.get(id))
        .filter((item): item is ActivityPickerItem => Boolean(item)),
    [itemMap, recentActivityIds],
  );

  const matchItems = useMemo(
    () => allItems.filter((item) => item.category === "matches"),
    [allItems],
  );

  const sessionItems = useMemo(
    () => allItems.filter((item) => item.category === "sessions"),
    [allItems],
  );

  const searchedItems = useMemo(() => {
    if (!normalizedQuery) return [];
    return allItems
      .filter((item) => item.searchText.includes(normalizedQuery))
      .sort((a, b) => {
        const scoreA = scoreActivitySearchMatch(a, normalizedQuery);
        const scoreB = scoreActivitySearchMatch(b, normalizedQuery);
        if (scoreA !== scoreB) return scoreA - scoreB;
        if (a.isRecent !== b.isRecent) return a.isRecent ? -1 : 1;
        if (a.isPopular !== b.isPopular) return a.isPopular ? -1 : 1;
        return a.activity.name.localeCompare(b.activity.name);
      });
  }, [allItems, normalizedQuery]);

  const rememberActivity = (activityId: ActivityDefinition["id"]) => {
    setRecentActivityIds((current) => {
      const next = [
        activityId,
        ...current.filter((id) => id !== activityId),
      ].slice(0, MAX_RECENT_SESSION_ACTIVITIES);
      try {
        window.localStorage.setItem(
          RECENT_SESSION_ACTIVITY_STORAGE_KEY,
          JSON.stringify(next),
        );
      } catch {
        // Ignore storage write failures
      }
      return next;
    });
  };

  const reset = () => {
    setQuery("");
    setFilter("popular");
  };

  return {
    query,
    setQuery,
    filter,
    setFilter,
    normalizedQuery,
    searchedItems,
    popularItems,
    recentItems,
    matchItems,
    sessionItems,
    allItems,
    rememberActivity,
    reset,
  };
}

export type ActivityPicker = ReturnType<typeof useActivityPicker>;
